import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";

const ROLES = ["admin", "owner", "kasir", "terapis"];

export default function UserManagementPage() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);

  async function load() {
    const { data } = await api.get("/users");
    setUsers(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEdit({ name: "", phone: "", role: "kasir", password: "", active: true, isNew: true }); }
  const openEdit = (u) => setEdit({ ...u, password: "" });

  async function save() {
    if (!edit.name || !edit.phone || (edit.isNew && !edit.password)) return toast.error("Required fields");
    try {
      if (edit.isNew) {
        await api.post("/users", { name: edit.name, phone: edit.phone, password: edit.password, role: edit.role });
      } else {
        const payload = { name: edit.name, phone: edit.phone, role: edit.role, active: edit.active };
        if (edit.password) payload.password = edit.password;
        await api.put(`/users/${edit.id}`, payload);
      }
      toast.success("Saved");
      setEdit(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  async function del(u) {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Deleted"); load();
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— Admin</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("users.title")}</h1>
        </div>
        <button onClick={openNew} data-testid="user-add-btn" className="btn-gold">
          <Plus className="h-4 w-4" /> {t("common.add")}
        </button>
      </div>

      <div className="card-luxury overflow-hidden">
        <table className="w-full text-sm desktop-table">
          <thead className="text-xs uppercase tracking-[0.15em] text-foreground/50 bg-charcoal-900/60">
            <tr>
              <th className="text-start px-4 py-3">{t("users.name")}</th>
              <th className="text-start px-4 py-3">{t("users.phone")}</th>
              <th className="text-start px-4 py-3">{t("users.role")}</th>
              <th className="text-start px-4 py-3">{t("users.status")}</th>
              <th className="text-end px-4 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-charcoal-600/40 hover:bg-gold/5" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-foreground/70">{u.phone}</td>
                <td className="px-4 py-3">
                  <span className="text-xs uppercase tracking-[0.15em] bg-gold/15 text-gold rounded px-2 py-1">{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs ${u.active ? "text-leaf" : "text-red-400"}`}>{u.active ? t("users.active") : t("users.inactive")}</span>
                </td>
                <td className="px-4 py-3 text-end space-x-2">
                  <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} className="p-2 rounded-full hover:bg-gold/10 text-gold inline-flex">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(u)} data-testid={`user-delete-${u.id}`} className="p-2 rounded-full hover:bg-red-500/10 text-red-400 inline-flex">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* MOBILE cards */}
        <div className="mobile-card-list p-3">
          {users.map((u) => (
            <div key={u.id} className="bg-charcoal-900/60 border border-gold/15 rounded-xl p-4" data-testid={`user-mcard-${u.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gold to-leaf grid place-items-center text-charcoal-900 font-bold shrink-0">
                    {u.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.name}</div>
                    <div className="text-xs text-foreground/50">{u.phone}</div>
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-1 shrink-0 bg-gold/15 text-gold`}>
                  {u.role}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gold/10">
                <span className={`text-xs ${u.active ? "text-leaf" : "text-red-400"}`}>● {u.active ? t("users.active") : t("users.inactive")}</span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(u)} className="p-2 rounded-full bg-gold/10 text-gold">
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(u)} className="p-2 rounded-full bg-red-500/10 text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEdit(null)}>
          <div className="card-luxury p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-heading">{edit.isNew ? t("common.add") : t("common.edit")} {t("users.title")}</h2>
              <button onClick={() => setEdit(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("users.name")}</label>
                <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("users.phone")}</label>
                <input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("users.role")}</label>
                <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">
                  {t("users.password")} {!edit.isNew && <span className="text-foreground/40">(optional)</span>}
                </label>
                <input type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              {!edit.isNew && (
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="accent-leaf h-4 w-4" />
                  {t("users.active")}
                </label>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEdit(null)} className="btn-outline-gold flex-1 justify-center">{t("common.cancel")}</button>
              <button onClick={save} data-testid="user-save-btn" className="btn-gold flex-1 justify-center">{t("common.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
